import React from "react";
import { Card, Button, Select, Input, InputNumber, Tooltip, Typography } from "antd";
import { PlusOutlined, StarOutlined, StarFilled, DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface LabelTemplate {
  id?: string;
  name: string;
  width: number;
  height: number;
  elements: any[];
  is_default?: boolean;
}

interface TemplatesSidebarProps {
  templates: LabelTemplate[];
  activeTemplate: LabelTemplate;
  setActiveTemplate: React.Dispatch<React.SetStateAction<LabelTemplate>>;
  setSelectedElementId: (id: string | null) => void;
  handleSetDefault: (id: string) => void;
  handleDeleteTemplate: (id: string) => void;
  products: any[];
  previewProduct: any;
  setPreviewProduct: (product: any) => void;
  costCodeAlphabet: string;
  setCostCodeAlphabet: (val: string) => void;
  expiryDaysOffset: number;
  setExpiryDaysOffset: (val: number) => void;
}

export const TemplatesSidebar: React.FC<TemplatesSidebarProps> = ({
  templates,
  activeTemplate,
  setActiveTemplate,
  setSelectedElementId,
  handleSetDefault,
  handleDeleteTemplate,
  products,
  previewProduct,
  setPreviewProduct,
  costCodeAlphabet,
  setCostCodeAlphabet,
  expiryDaysOffset,
  setExpiryDaysOffset
}) => {
  return (
    <div className="space-y-6">
      <Card 
        title="Saved Templates" 
        size="small"
        className="shadow-sm border border-gray-200 rounded-lg bg-white"
        extra={
          <Button 
            type="link" 
            size="small" 
            icon={<PlusOutlined />}
            onClick={() => {
              setActiveTemplate({
                name: "New Template " + (templates.length + 1),
                width: 50,
                height: 30,
                elements: []
              });
              setSelectedElementId(null);
            }}
          >
            New
          </Button>
        }
      >
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {templates.map(t => (
            <div 
              key={t.id} 
              className={`flex justify-between items-center p-2.5 rounded-md border cursor-pointer transition ${activeTemplate.id === t.id ? "bg-indigo-50/50 border-indigo-200" : "border-gray-100 hover:bg-gray-50"}`}
              onClick={() => {
                setActiveTemplate(t);
                setSelectedElementId(null);
              }}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <Tooltip title={t.is_default ? "Default Template" : "Set as Default"}>
                  <span onClick={(e) => {
                    e.stopPropagation();
                    if (t.id) handleSetDefault(t.id);
                  }}>
                    {t.is_default ? <StarFilled className="text-yellow-500" /> : <StarOutlined className="text-gray-400 hover:text-yellow-500" />}
                  </span>
                </Tooltip>
                <span className="truncate text-xs font-semibold text-gray-700">{t.name}</span>
              </div>
              <Button 
                type="text" 
                danger 
                size="small" 
                icon={<DeleteOutlined className="text-xs" />} 
                onClick={(e) => {
                  e.stopPropagation();
                  if (t.id) handleDeleteTemplate(t.id);
                }}
              />
            </div>
          ))}
          {templates.length === 0 && (
            <Text type="secondary" className="text-center block py-4 text-xs">No saved templates</Text>
          )}
        </div>
      </Card>

      <Card title="Preview Configurations" size="small" className="shadow-sm border border-gray-200 rounded-lg bg-white">
        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-gray-500 block mb-1">Select Product</label>
            <Select
              className="w-full text-xs"
              showSearch
              placeholder="Select a product"
              optionFilterProp="children"
              value={previewProduct?.id}
              onChange={(val) => setPreviewProduct(products.find(p => p.id === val))}
            >
              {products.map(p => (
                <Select.Option key={p.id} value={p.id}>{p.title}</Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-[11px] font-semibold text-gray-500 block mb-1">Cost Code Cipher Key</label>
            <Input 
              value={costCodeAlphabet} 
              onChange={(e) => setCostCodeAlphabet(e.target.value)} 
              maxLength={10} 
              placeholder="COMPUTERSX"
              className="uppercase font-mono text-xs"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-gray-500 block mb-1">Expiry Offset (Days)</label>
            <InputNumber 
              className="w-full text-xs" 
              value={expiryDaysOffset} 
              onChange={(val) => setExpiryDaysOffset(val || 0)} 
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
